import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { TALK_TO_SALES_URL } from '../../lib/links';

/* Two ways in for a buyer who is not ready to talk to sales yet: read the
 rollout playbook, or watch the tour. Deliberately only two, so the choice
 stays a choice. */

const TOUR_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/cf56c2dd-56b8-4eeb-b4d6-b164d4ddd9de.jpg';
const CALENDLY = TALK_TO_SALES_URL;
function CtaRow({
  label,
  tone
}: {
  label: string;
  tone: 'light' | 'dark';
}) {
  return <span className="mt-6 inline-flex items-center gap-3 font-semibold" data-id="element-3008" data-mp="2re2x13x4tf01">
      <span className={`flex h-9 w-9 items-center justify-center rounded-full transition-transform group-hover:translate-x-0.5 ${tone === 'dark' ? 'bg-white/20 text-white' : 'bg-black/10 text-[#2B211A]'}`} data-id="element-3009" data-mp="2mf9hl5yabdx8">
        <ArrowRightIcon size={16} strokeWidth={2.5} data-id="element-3010" data-mp="1b2yy5q5fku5g" />
      </span>
      {label}
    </span>;
}

/* A stylised spread of the handbook — two pages, one cover and one inner
 page — cropped by the card so it reads as an object sitting in the tile. */
function HandbookVisual() {
  return <div className="mt-auto flex justify-center gap-3 pt-10" data-id="element-3011" data-mp="mdpbop64pwlj">
      <div className="w-[46%] rounded-t-sm bg-[#2B211A] p-4 shadow-[0_-8px_30px_-10px_rgba(0,0,0,0.3)]" data-id="element-3012" data-mp="7hzseysbvxki">
        <p className="font-title text-lg font-medium leading-tight text-white" data-id="element-3013" data-mp="cgb3fcud0e4b">
          Transformation
        </p>
        <p className="mt-16 font-title text-base font-medium leading-tight text-white" data-id="element-3014" data-mp="3utsgmn5ux766">
          20% technology
          <br data-id="element-3015" data-mp="3gxrmdirjdgf" />
          and 80% people
        </p>
      </div>
      <div className="w-[46%] rounded-t-sm bg-white p-4 shadow-[0_-8px_30px_-10px_rgba(0,0,0,0.2)]" data-id="element-3016" data-mp="3kx5y45106s7b">
        <p className="text-[13px] font-bold text-[#2B211A]" data-id="element-3017" data-mp="3pv7hkr2cctjb">
          Manage change actively
        </p>
        <div className="mt-3 space-y-1.5" aria-hidden="true" data-id="element-3018" data-mp="3bvplrrqmog0g">
          {[100, 92, 96, 78, 100, 88, 94, 70, 98, 84].map((w, i) => <span key={i} className="block h-[3px] rounded-full bg-neutral-200" style={{
          width: `${w}%`
        }} data-id="element-3019" data-mp="3fw9l91yobvpr" data-mpi={i} />)}
        </div>
      </div>
    </div>;
}
export function GuidedStart() {
  return <section className="px-6 py-20 md:px-10 md:py-28" data-id="element-3020" data-mp="1hnc3ribbyhsi">
      <div className="mx-auto max-w-[1100px]" data-id="element-3021" data-mp="1corbc52bl2em">
        <h2 className="font-title text-3xl font-medium leading-tight tracking-tight md:text-5xl" data-id="element-3022" data-mp="1cpk9wir4kllo">
          Guiding you step by step.
          <span className="block text-neutral-400" data-id="element-3023" data-mp="17o3e31nhzpby">
            Learn how Giga can work for your organisation.
          </span>
        </h2>

        <div className="mt-12 grid gap-5 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]" data-id="element-3024" data-mp="20fn0svjazlip">
          {/* Handbook */}
          <a href={CALENDLY} target="_blank" rel="noopener noreferrer" className="group flex flex-col overflow-hidden rounded-2xl bg-[#D5DCDA] p-7 pb-0 text-[#2B211A] md:p-8 md:pb-0" data-id="element-3025" data-mp="1vdxn184p4tqx">
            <h3 className="font-title text-xl font-medium tracking-tight md:text-2xl" data-id="element-3026" data-mp="k1en7qgigmj7">
              AI Adoption Handbook
            </h3>
            <p className="mt-2 max-w-[34ch] leading-relaxed text-[#2B211A]/60" data-id="element-3027" data-mp="g0utyxz2632c">
              Our best practices from supporting hundreds of AI rollouts.
            </p>
            <CtaRow label="Get the handbook" tone="light" data-id="element-3028" data-mp="2voi9dc86lzfr" />
            <HandbookVisual data-id="element-3029" data-mp="30n1sl4ve4x4f" />
          </a>

          {/* Product tour */}
          <Link to="/demo" className="group relative flex min-h-[420px] flex-col justify-end overflow-hidden rounded-2xl p-7 text-white md:p-8" data-id="element-3030" data-mp="30kytzwg69vo3">
            <img src={TOUR_IMAGE} alt="A monitor on a desk in a warm, sunlit office" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" data-id="element-3031" data-mp="3abjrfgk40u5f" />
            {/* Scrim keeps the copy legible against the photo. */}
            <div className="absolute inset-0 bg-black/45" aria-hidden="true" data-id="element-3032" data-mp="3oc4beg1rgw0i" />
            <div className="relative" data-id="element-3033" data-mp="3oa09u4rc493e">
              <h3 className="font-title text-xl font-medium tracking-tight md:text-2xl" data-id="element-3034" data-mp="1p01v1eofohr2">
                Watch product tour
              </h3>
              <p className="mt-2 max-w-[38ch] leading-relaxed text-white/70" data-id="element-3035" data-mp="1tylhrhaupxkh">
                See a 4-minute overview of Giga's core products.
              </p>
              <CtaRow label="See Giga in action" tone="dark" data-id="element-3036" data-mp="tpvcj8isyjo4" />
            </div>
          </Link>
        </div>
      </div>
    </section>;
}